/**
 * Global store for page-level companion action callbacks.
 *
 * Instead of each page mounting useCompanionActions directly (which assumes a
 * single subscriber), pages register their callbacks here via
 * useRegisterCompanionActions. The single global CompanionActionHost reads this
 * store and is the only caller of useCompanionActions.
 *
 * This is a plain .ts file (not .tsx) because it exports a hook alongside the
 * store — .tsx files must only export React components (Vite Fast Refresh).
 */

import { create } from 'zustand'
import { useEffect } from 'react'
import type { ActionCallbacks } from './types'

interface ActionRegistrationState {
  scope: string | null
  callbacks: ActionCallbacks | null
  register: (scope: string, callbacks: ActionCallbacks) => void
  /**
   * Clears the store only when the stored scope matches the argument.
   *
   * Race guard for route transitions: the new page may call register() before
   * the old page's component unmounts and calls unregister(). If we cleared
   * unconditionally, the old page's cleanup would clobber the new page's
   * registration.
   */
  unregister: (scope: string) => void
}

export const useActionRegistrationStore = create<ActionRegistrationState>((set, get) => ({
  scope: null,
  callbacks: null,
  register: (scope, callbacks) => {
    set({ scope, callbacks })
  },
  unregister: (scope) => {
    if (get().scope === scope) {
      set({ scope: null, callbacks: null })
    }
  },
}))

/**
 * Registers page-level action callbacks with the global CompanionActionHost.
 *
 * Replace each page's `useCompanionActions(callbacks)` call with:
 *   `useRegisterCompanionActions('scope-name', callbacks)`
 *
 * The host reads the registered callbacks on each render and passes them to
 * useCompanionActions, which is mounted once globally in app-layout.tsx.
 *
 * Registration happens on mount and whenever scope/callbacks change.
 * Unregistration (with race guard) happens on unmount.
 */
export function useRegisterCompanionActions(
  scope: string,
  callbacks: ActionCallbacks | null,
): void {
  useEffect(() => {
    if (callbacks !== null) {
      useActionRegistrationStore.getState().register(scope, callbacks)
    }
    return () => {
      useActionRegistrationStore.getState().unregister(scope)
    }
  }, [scope, callbacks])
}
